import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Emblem } from "./Emblem";
import { FirmSignature } from "./FirmSignature";
import { useLanguage } from "@/i18n/LanguageContext";

const DURATION = 8;

export const SplashOverlay = ({ open, onEnter }) => {
  const { t } = useLanguage();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!open) return;
    const start = Date.now();
    const tick = setInterval(() => {
      const p = Math.min((Date.now() - start) / (DURATION * 1000), 1);
      setProgress(p);
      if (p >= 1) {
        clearInterval(tick);
        onEnter();
      }
    }, 50);
    return () => clearInterval(tick);
  }, [open, onEnter]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="splash-overlay"
          className="fixed inset-0 z-[100] flex flex-col bg-navy-deep"
          exit={{ y: "-100%" }}
          transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
        >
          <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{ background: "radial-gradient(ellipse 70% 50% at 50% 35%, #D4AF37, transparent 65%)" }} />
          <div className="flex items-center justify-between px-6 py-5 sm:px-12 border-b border-gold/10">
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500">{t.splash.est}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold/80">{t.splash.states}</span>
          </div>

          <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-6 py-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="mb-5 flex h-16 w-16 items-center justify-center text-gold sm:mb-8 sm:h-24 sm:w-24"
            >
              <Emblem className="h-16 w-14 sm:h-24 sm:w-[5.5rem]" strokeWidth={2} />
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }}
              className="mb-5 font-mono text-[11px] uppercase tracking-[0.4em] text-gold"
            >
              LexAegis International
            </motion.p>

            <div className="max-w-3xl overflow-hidden">
              <motion.h1
                key={t.splash.copy}
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ delay: 0.45, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif text-xl font-medium leading-snug text-slate-100 sm:text-3xl lg:text-[2.75rem]"
              >
                {t.splash.copy}
              </motion.h1>
            </div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-8 flex flex-col items-center gap-2"
            >
              <div className="gold-hairline w-40" />
              <FirmSignature size="text-base sm:text-2xl lg:text-3xl" testid="splash-firm-signature" />
            </motion.div>

            <motion.button
              data-testid="splash-enter-button"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.7 }}
              onClick={onEnter}
              className="group mt-6 inline-flex items-center gap-3 border border-gold/50 px-8 py-3.5 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-deep sm:mt-12"
            >
              {t.splash.enter}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </div>

          <div className="px-6 pb-6 sm:px-12">
            <div className="h-px w-full bg-navy-border">
              <div className="h-px bg-gold transition-[width] duration-100 ease-linear" style={{ width: `${progress * 100}%` }} />
            </div>
            <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              <span>{t.splash.convention}</span>
              <span>{t.splash.auto}</span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
